import { constVoid, DOM, IOO, O, pipe, RNEA } from '@packages/prelude'

type VideoState = 'idle' | 'playing' | 'preview'

interface VideoController {
  readonly element: HTMLIFrameElement | HTMLVideoElement
  state: VideoState
  play: () => void
  pause: () => void
  reset: () => void
}

let allControllers: ReadonlyArray<VideoController> = []
let youtubeAPIReady = false
let youtubeAPILoading = false
let youtubePendingElements: ReadonlyArray<HTMLIFrameElement> = []

const stopOtherControllers = (activeController: VideoController): void => {
  allControllers.forEach(controller => {
    if (controller !== activeController && controller.state !== 'idle') {
      controller.pause()
      controller.reset()
      controller.state = 'idle'
    }
  })
}

const initializeLocalVideo = (element: HTMLVideoElement): VideoController => {
  const videoBlock = element.closest('.video-block')
  let programmaticPlay = false

  const controller: VideoController = {
    element,
    state: 'idle',
    play: () => {
      programmaticPlay = true
      element.muted = true
      element
        .play()
        .then(() => {
          if (controller.state === 'idle') {
            controller.state = 'preview'
          }
        })
        .catch(() => {
          controller.state = 'idle'
          programmaticPlay = false
        })
    },
    pause: () => {
      element.pause()
    },
    reset: () => {
      element.currentTime = 0.5 // Since the placeholder image is not restored when player is reset, we need to skip to one of the first frames to avoid black video frame, if video fades in from black
    }
  }

  videoBlock?.addEventListener('mouseenter', () => {
    if (controller.state === 'idle') {
      stopOtherControllers(controller)
      controller.play()
    }
  })

  videoBlock?.addEventListener('mouseleave', () => {
    if (controller.state === 'preview') {
      controller.pause()
      controller.reset()
      controller.state = 'idle'
    }
  })

  element.addEventListener('play', () => {
    if (programmaticPlay) {
      programmaticPlay = false
    } else {
      stopOtherControllers(controller)
      controller.state = 'playing'
      controller.reset()
      element.muted = false
    }
  })

  element.addEventListener('pause', () => {
    if (controller.state === 'playing') {
      controller.state = 'idle'
    }
  })

  element.addEventListener('ended', () => {
    controller.state = 'idle'
  })

  allControllers = allControllers.concat(controller)

  return controller
}

const initializeYouTubeVideo = (element: HTMLIFrameElement): void => {
  if (youtubeAPIReady) {
    createYouTubePlayer(element)
  } else {
    youtubePendingElements = [...youtubePendingElements, element]
    loadYouTubeAPI()
  }
}

const createYouTubePlayer = (element: HTMLIFrameElement): void => {
  try {
    const videoBlock = element.closest('.video-block')
    let programmaticPlay = false

    const player = new YT.Player(element, {
      events: {
        onReady: () => {
          player.mute()

          const controller: VideoController = {
            element,
            state: 'idle',
            play: () => {
              programmaticPlay = true
              player.mute()
              player.playVideo()
            },
            pause: () => {
              player.pauseVideo()
            },
            reset: () => {
              player.seekTo(0, true)
            }
          }

          videoBlock?.addEventListener('mouseenter', () => {
            if (controller.state === 'idle') {
              stopOtherControllers(controller)
              controller.state = 'preview'
              controller.play()
            }
          })

          videoBlock?.addEventListener('mouseleave', () => {
            if (controller.state === 'preview') {
              controller.pause()
              controller.reset()
              controller.state = 'idle'
            }
          })

          allControllers = allControllers.concat(controller)
        },
        onStateChange: (event: YT.OnStateChangeEvent) => {
          const controller = allControllers.find(c => c.element === element)
          if (!controller) return

          if (event.data === YT.PlayerState.PLAYING) {
            if (programmaticPlay) {
              programmaticPlay = false
            } else {
              stopOtherControllers(controller)
              controller.state = 'playing'
              player.unMute()
              player.seekTo(0, true)
            }
          } else if (event.data === YT.PlayerState.PAUSED) {
            if (controller.state === 'playing') {
              controller.state = 'idle'
            }
          } else if (event.data === YT.PlayerState.ENDED) {
            controller.state = 'idle'
          }
        }
      }
    })
  } catch (error) {
    console.error('Failed to create YouTube player:', error)
  }
}

const loadYouTubeAPI = (): void => {
  if (youtubeAPILoading) return
  youtubeAPILoading = true

  if (typeof YT !== 'undefined' && typeof YT.Player !== 'undefined') {
    onYouTubeAPIReady()

    return
  }

  const tag = document.createElement('script')
  tag.src = 'https://www.youtube.com/iframe_api'
  const firstScriptTag = document.getElementsByTagName('script')[0]
  if (firstScriptTag?.parentNode) {
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
  } else {
    document.head.appendChild(tag)
  }

  ;(window as any).onYouTubeIframeAPIReady = onYouTubeAPIReady
}

const onYouTubeAPIReady = (): void => {
  youtubeAPIReady = true
  youtubePendingElements.forEach(createYouTubePlayer)
  youtubePendingElements = []
}

const initializeVideoHoverPreview = (element: HTMLElement): void => {
  pipe(
    O.fromNullable(element.dataset.videoHover),
    O.map(videoType => {
      switch (videoType) {
        case 'local':
          if (element instanceof HTMLVideoElement) {
            initializeLocalVideo(element)
          }
          break
        case 'youtube':
          if (element instanceof HTMLIFrameElement) {
            initializeYouTubeVideo(element)
          }
          break
      }
    })
  )
}

const initializeAllVideoHoverPreviews = (): void => {
  pipe(
    IOO.of({ document }),
    IOO.chain(
      ({ document }) =>
        pipe(document, DOM.querySelectorAll('.video-block [data-video-hover]')) as unknown as IOO.IOOption<
          RNEA.ReadonlyNonEmptyArray<HTMLElement>
        >
    ),
    IOO.fold(
      () => constVoid,
      elements =>
        IOO.of(
          pipe(
            elements,
            RNEA.map(element => {
              initializeVideoHoverPreview(element)

              return element
            })
          )
        )
    )
  )()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAllVideoHoverPreviews)
} else {
  initializeAllVideoHoverPreviews()
}
